<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <div class="text-center">
        <h2 class="mt-6 text-3xl font-extrabold text-gray-900">Create your account</h2>
        <p class="mt-2 text-sm text-gray-600">
          Or
          <router-link
            to="/auth/login"
            class="font-medium text-blue-600 hover:text-blue-500 transition-colors"
          >
            sign in to your existing account
          </router-link>
        </p>
      </div>

      <!-- Notification for existing user -->
      <div v-if="showExistingUserAlert" class="rounded-md bg-blue-50 p-4 mb-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-blue-800">Account already exists</h3>
            <div class="mt-2 text-sm text-blue-700">
              <p>
                An account with this email already exists. We're redirecting you to verify your
                email address to complete your account setup.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Backend created the account but it is pending admin approval.
           Show waitlist prompt for vetting context. Copy is FE-owned;
           waitlist URL + support email are config values from the BE. -->
      <div
        v-if="pendingApproval"
        class="rounded-md bg-amber-50 border border-amber-200 p-6 mb-6 space-y-4"
      >
        <h3 class="text-lg font-semibold text-amber-900">
          Account created — pending approval
        </h3>
        <p class="text-sm text-amber-800">
          Your account is awaiting review. We'll email you once it's enabled —
          usually within 1–3 business days. Sharing your use case below speeds
          things up.
        </p>
        <a
          v-if="pendingApproval.waitlist_url"
          :href="pendingApproval.waitlist_url"
          target="_blank"
          rel="noopener"
          class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700"
        >
          Tell us about your use case ↗
        </a>
        <p v-if="pendingApproval.support_email" class="text-xs text-amber-800">
          Questions? Email
          <a
            :href="`mailto:${pendingApproval.support_email}?subject=Account%20approval`"
            class="underline"
          >{{ pendingApproval.support_email }}</a>.
        </p>
      </div>

      <div v-else class="mt-8">
        <RegisterForm
          @success="handleRegisterSuccess"
          @terms="router.push('/terms')"
          @privacy="router.push('/privacy')"
          @login="router.push('/auth/login')"
          @verification-needed="handleVerificationNeeded"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useAuth } from '@/features/auth/composables/useAuth';
  import { RegisterForm } from '@/features/auth/components';

  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const showExistingUserAlert = ref(false);

  // Populated when the backend creates the account but it requires admin
  // approval (closed-beta gate). Replaces the form with a "tell us about
  // your use case" prompt linking to the waitlist Google Form.
  const pendingApproval = ref<{ waitlist_url: string; support_email: string; message: string } | null>(null);

  // Handle successful registration. Backend response carries pending_approval
  // when SIGNUP_REQUIRES_APPROVAL=true; route to waitlist banner instead of
  // the verify-email page (no verification email is sent until approval).
  const handleRegisterSuccess = (response: unknown) => {
    const r = response as {
      email?: string;
      pending_approval?: boolean;
      waitlist_url?: string;
      support_email?: string;
      message?: string;
    };
    if (r?.pending_approval) {
      pendingApproval.value = {
        waitlist_url: r.waitlist_url ?? '',
        support_email: r.support_email ?? '',
        message: r.message ?? 'Your account is pending approval. We will email you once it is enabled.',
      };
      return;
    }
    router.push({
      path: '/auth/verify-email',
      query: { email: r?.email },
    });
  };

  // Handle verification needed (user already exists but not verified)
  const handleVerificationNeeded = (email: string) => {
    // Show alert briefly before redirecting
    showExistingUserAlert.value = true;

    // Redirect to verification page with email after a brief delay
    setTimeout(() => {
      router.push({
        path: '/auth/verify-email',
        query: { email },
      });
    }, 2000);
  };

  // Redirect if already authenticated
  onMounted(() => {
    if (isAuthenticated.value) {
      router.push('/app');
    }
  });
</script>
